import React from 'react'
import Chip from '@mui/material/Chip'

/**
 * Carryover-backlog banner for the To Process tab. Rendered ONLY when the loaded queue
 * contains overdue (past-due, not-yet-processed) candidates — otherwise it stays out of
 * the way. It names the total and breaks the backlog down per business day so a missed
 * weekend / prior day is impossible to overlook, and each day chip narrows the table to
 * exactly that day (click again, or "Show all dates", to clear the filter).
 *
 * All date math is done upstream in businessTimeZone (see buildOverdueBreakdown); this is
 * a pure presentation component.
 */
const BacklogBanner = ({ total = 0, breakdown = [], activeDay, onSelectDay, onShowAll }) => {
  if (!total || breakdown.length === 0) return null

  const dueFilterActive = activeDay && activeDay !== 'all'

  return (
    <div
      className="rounded-2xl border border-warning-main/40 bg-warning-main/5 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2"
      role="status"
      data-testid="backlog-banner"
    >
      <span className="text-lg leading-none" aria-hidden>
        &#9888;
      </span>
      <p className="text-sm text-text-primary">
        <span className="font-semibold">{total}</span>{' '}
        {total === 1 ? 'payment' : 'payments'} due earlier not yet processed
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {breakdown.map(({ dateKey, count, label }) => {
          const active = activeDay === dateKey
          return (
            <Chip
              key={dateKey}
              size="small"
              data-testid={`backlog-day-${dateKey}`}
              aria-pressed={active}
              color={active ? 'warning' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => (active ? onShowAll?.() : onSelectDay?.(dateKey))}
              label={
                <span className="inline-flex items-center gap-1.5">
                  <span>{label}</span>
                  <span className="inline-flex min-w-[18px] justify-center rounded-full bg-warning-main/20 px-1 text-[11px] leading-4 text-warning-main">
                    {count}
                  </span>
                </span>
              }
            />
          )
        })}
      </div>
      {dueFilterActive && (
        <button
          type="button"
          className="ml-auto text-xs font-medium text-primary-main hover:underline"
          data-testid="backlog-show-all"
          onClick={() => onShowAll?.()}
        >
          Show all dates
        </button>
      )}
    </div>
  )
}

export default BacklogBanner
